import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { DndContext, closestCenter, useDraggable, useDroppable } from '@dnd-kit/core'
import { SortableContext, rectSortingStrategy } from '@dnd-kit/sortable'
import FileCopyIcon from '@mui/icons-material/FileCopy'
import FlipToFrontIcon from '@mui/icons-material/FlipToFront'
import LoadingButton from '@mui/lab/LoadingButton'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import Grid from '@mui/material/Grid'
import MenuItem from '@mui/material/MenuItem'
import Select from '@mui/material/Select'
import Skeleton from '@mui/material/Skeleton'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import grey from '@mui/material/colors/grey'

import { useAppSelector } from '~/store'
import { get, post } from '~/tools/request'
import { Alert } from '@mui/material'

function getFulfillmentLineItems(fulfillment) {
    // If the fulfillment line item is for a bundle, use the bundle item quanties
    // instead of fli.quantity which is for the member item
    const flis = []
    const bundleByItemId = {}
    fulfillment.fulfillment_line_items.forEach((fli) => {
        fli.key = `${fli.sale_line_item}-${fli.stock_record.stock_item.sku}`

        if (!fli.bundle_info) {
            flis.push(fli)
            return
        }

        let bundleFli = bundleByItemId[fli.bundle_info.bundle_stock_item_id]
        if (!bundleFli) {
            bundleFli = {
                is_bundle: true,
                id: fli.id,
                key: `${fli.sale_line_item}-${fli.bundle_info.bundle_stock_item_sku}`,
                sku: fli.bundle_info.bundle_stock_item_sku,
                quantity: fli.bundle_info.bundle_quantity,
                name: fli.bundle_info.bundle_stock_item_name,
                image_url: fli.image_url,
                members: [],
            }
            flis.push(bundleFli)
            bundleByItemId[fli.bundle_info.bundle_stock_item_id] = bundleFli
        }
        bundleFli.members.push({
            ...fli,
            units_per_bundle: fli.quantity / fli.bundle_info.bundle_quantity,
        })
    })

    return flis
}

function breakdownFulfillmentLineItemsIntoMembers(fulfillment) {
    // The opposite of getFulfillmentLineItems
    // Convert back into member item quantities when saving
    const flis = []
    fulfillment.fulfillment_line_items.forEach((fli) => {
        if (!fli.is_bundle) {
            flis.push(fli)
            return
        }
        fli.members.forEach((memberFli) => {
            flis.push({
                ...memberFli,
                quantity: fli.quantity * memberFli.units_per_bundle,
            })
        })
    })

    return flis
}

function moveItemToFulfillmentSection(fulfillment, item) {
    const existingItem = fulfillment.fulfillment_line_items.find((i) => i.key === item.key)
    if (existingItem) {
        // merge items with matching keys
        existingItem.quantity += item.quantity
    } else {
        fulfillment.fulfillment_line_items.push({ ...item })
    }
}

export default function FulfillmentSplitModal({ sale, onClose }) {
    const { t } = useTranslation(['fulfillment_split'])

    const [fulfillmentSections, setFulfillmentSections] = useState({})
    const [shippingProviders, setShippingProviders] = useState([])
    const [quantityDialogOpen, setQuantityDialogOpen] = useState(false)
    const [loading, setLoading] = useState(true)
    const [submitting, setSubmitting] = useState(false)

    const [draggingItem, setDraggingItem] = useState(null)
    const [hoveringOverItem, setHoveringOverItem] = useState(null)
    const [assignedShippingProviders, setAssignedShippingProviders] = useState({})
    const [errorMessage, setErrorMessage] = useState(null)

    const activeFulfillmentLineItem = useMemo(() => {
        if (!draggingItem) return null
        const fulfillment = fulfillmentSections[draggingItem.sectionId]
        return fulfillment
            ? fulfillment.fulfillment_line_items.find((fli) => fli.id === draggingItem.itemId)
            : null
    }, [draggingItem, fulfillmentSections])

    const { saleFulfillmentsAPIURL, shippingProvidersForStoreAPIURL } = useAppSelector(
        (state) => state.initial.endpoints
    )
    useEffect(() => {
        async function fetchFulfillments() {
            setLoading(true)
            const res = await get(saleFulfillmentsAPIURL.replace('sale_pk', sale.id))
            const fulfillmentById = {}
            const assignedProviders = {}

            res.results.forEach((f) => {
                f.fulfillment_line_items = getFulfillmentLineItems(f)
                fulfillmentById[f.id] = f

                if (f.is_shipping_created_externally && f.shipping_provider?.id) {
                    assignedProviders[f.id] = {
                        is_shipping_created_externally: true,
                        shipping_provider: { ...f.shipping_provider },
                    }
                }
            })

            setFulfillmentSections(fulfillmentById)
            setAssignedShippingProviders(assignedProviders)
            setLoading(false)
        }

        function fetchShippingProviders() {
            get(
                shippingProvidersForStoreAPIURL.replace(
                    'store_pk',
                    sale.shop.store?.id || sale.shop.store_id
                )
            ).then((res) => {
                setShippingProviders(res.results)
            })
        }

        fetchFulfillments()
        fetchShippingProviders()
    }, [sale, saleFulfillmentsAPIURL, shippingProvidersForStoreAPIURL])

    const handleDragStart = (event) => {
        setDraggingItem(event.active.data.current)
    }

    const handleDragEnd = (event) => {
        const { active, over } = event
        if (!over) return

        const activeSectionId = active.data.current.sectionId
        const overSectionId = over.data.current.sectionId

        if (
            active.data.current.itemId == draggingItem.itemId &&
            activeSectionId !== overSectionId
        ) {
            if (
                (activeFulfillmentLineItem && activeFulfillmentLineItem.quantity === 1) ||
                overSectionId === 'split_equally'
            ) {
                moveItem()
                setDraggingItem(null)
                setHoveringOverItem(null)
            } else {
                setHoveringOverItem(over.data.current)
                setQuantityDialogOpen(true)
            }
        } else {
            setDraggingItem(null)
            setHoveringOverItem(null)
        }
    }

    const handleDragOver = (event) => {
        const { active, over } = event
        if (!over) return

        setHoveringOverItem(over.data.current)
    }

    const moveItem = (quantity) => {
        setFulfillmentSections((prev) => {
            const activeFulfillment = prev[draggingItem.sectionId]
            const providerInfo = assignedShippingProviders[draggingItem.sectionId]

            if (hoveringOverItem.sectionId === 'split_equally') {
                let selectedItem = null
                activeFulfillment.fulfillment_line_items =
                    activeFulfillment.fulfillment_line_items.filter((item) => {
                        if (item.id === draggingItem.itemId) {
                            selectedItem = item
                            return false
                        }
                        return true
                    })
                let quantity = selectedItem.quantity
                if (activeFulfillment.fulfillment_line_items.length === 0) {
                    activeFulfillment.fulfillment_line_items = [{ ...selectedItem, quantity: 1 }]
                    quantity -= 1
                }
                for (let i = 0; i < quantity; i++) {
                    const newFulfillment = createNewFulfillment()
                    newFulfillment.id -= i
                    newFulfillment.fulfillment_line_items.push({ ...selectedItem, quantity: 1 })

                    // Copy shipping provider if the original had one pre-assigned
                    if (providerInfo) {
                        newFulfillment.is_shipping_created_externally =
                            providerInfo.is_shipping_created_externally
                        newFulfillment.shipping_provider = providerInfo.shipping_provider
                    }

                    prev[newFulfillment.id] = newFulfillment
                }

                return {
                    ...prev,
                    [activeFulfillment.id]: activeFulfillment,
                }
            }

            let overFulfillment = null
            if (hoveringOverItem.sectionId === 'new_fulfillment') {
                overFulfillment = createNewFulfillment()

                // Copy shipping provider if the original had one pre-assigned
                if (providerInfo) {
                    overFulfillment.is_shipping_created_externally =
                        providerInfo.is_shipping_created_externally
                    overFulfillment.shipping_provider = providerInfo.shipping_provider
                }
            } else {
                overFulfillment = prev[hoveringOverItem.sectionId]
            }
            const activeFulfillmentItems = []
            activeFulfillment.fulfillment_line_items.forEach((item) => {
                if (item.id === draggingItem.itemId) {
                    if (quantity > 0 && item.quantity > quantity) {
                        item.quantity -= quantity
                        activeFulfillmentItems.push(item)
                        moveItemToFulfillmentSection(overFulfillment, { ...item, quantity })
                    } else {
                        moveItemToFulfillmentSection(overFulfillment, item)
                    }
                } else {
                    activeFulfillmentItems.push(item)
                }
            })
            activeFulfillment.fulfillment_line_items = activeFulfillmentItems
            const newSections = {
                ...prev,
                [draggingItem.sectionId]: activeFulfillment,
                [overFulfillment.id]: overFulfillment,
            }
            if (activeFulfillment.fulfillment_line_items.length === 0 && activeFulfillment.id < 0) {
                // remove the newly created fulfillment as it no longer contains any items
                delete newSections[draggingItem.sectionId]
            }
            return newSections
        })
    }

    const createNewFulfillment = () => {
        const minId = Math.min(...Object.keys(fulfillmentSections), 0)
        const newId = minId - 1
        return {
            id: newId,
            fulfillment_id: '',
            shipping_provider: {},
            fulfillment_line_items: [],
        }
    }

    const handleShippingProviderChange = (fulfillmentId, shippingProviderId) => {
        const fulfillment = fulfillmentSections[fulfillmentId]
        if (!fulfillment.shipping_provider) {
            fulfillment.shipping_provider = {}
        }
        fulfillment.shipping_provider.id = shippingProviderId
        setFulfillmentSections({ ...fulfillmentSections, [fulfillmentId]: fulfillment })
    }

    const closeQuantityDialog = (quantity) => {
        if (Number.isInteger(quantity) && quantity > 0) {
            moveItem(quantity)
        }
        setDraggingItem(null)
        setHoveringOverItem(null)
        setQuantityDialogOpen(false)
    }

    const handleSaveFulfillments = async () => {
        setSubmitting(true)
        const payload = Object.values(fulfillmentSections).map((fulfillment) => {
            return {
                id: fulfillment.id > 0 ? fulfillment.id : undefined,
                shipping_provider_id: fulfillment.shipping_provider?.id
                    ? fulfillment.shipping_provider.id
                    : null,
                fulfillment_line_items: breakdownFulfillmentLineItemsIntoMembers(fulfillment).map(
                    (fli) => ({
                        id: fli.id,
                        quantity: fli.quantity,
                    })
                ),
            }
        })

        const res = await post(saleFulfillmentsAPIURL.replace('sale_pk', sale.id), payload)
        setSubmitting(false)
        if (res.ok) {
            setErrorMessage(null)
            onClose({ sale_pk: sale.id, fulfillments: res.fulfillments })
        } else {
            setErrorMessage(res.error_message)
        }
    }

    const mergeAllFulfillments = () => {
        const fulfillments = Object.values(fulfillmentSections)
        if (fulfillments.length <= 1) return

        const firstFulfillment = Object.values(fulfillments)[0]
        for (let i = 1; i < fulfillments.length; i++) {
            fulfillments[i].fulfillment_line_items.forEach((item) => {
                moveItemToFulfillmentSection(firstFulfillment, item)
            })
        }
        setFulfillmentSections({
            [firstFulfillment.id]: firstFulfillment,
        })
    }

    const setAllShippingProvider = (shippingProviderId) => {
        Object.values(fulfillmentSections).forEach((fulfillment) => {
            if (!fulfillment.shipping_provider) {
                fulfillment.shipping_provider = {}
            }
            fulfillment.shipping_provider.id = shippingProviderId
        })
        setFulfillmentSections({ ...fulfillmentSections })
    }

    const numAssignableShippingProviders = Object.values(fulfillmentSections).filter(
        (f) => !f.is_shipping_created_externally || f.shipping_provider?.id
    ).length

    return (
        <Dialog
            open
            scroll="paper"
            fullWidth
            PaperProps={{ sx: { width: '700px', maxWidth: '100%' } }}
            onClose={onClose}
        >
            <DialogTitle
                sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
            >
                {t('dialog_title')}
            </DialogTitle>
            <DialogContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <div style={{ width: '100%' }}>
                    {errorMessage && (
                        <Alert severity="error" style={{ marginBottom: 20 }}>
                            {t('error')}: {errorMessage}
                        </Alert>
                    )}
                    <div style={{ color: grey[500], marginBottom: 24 }}>{t('instructions')}</div>
                    {numAssignableShippingProviders > 1 && (
                        <div
                            style={{
                                display: 'flex',
                                marginBottom: 24,
                                alignItems: 'center',
                                gap: 8,
                            }}
                        >
                            <div style={{ color: grey[500] }}>{t('set_all')}</div>
                            <div>
                                <Select
                                    label={t('shipping_provider')}
                                    style={{ width: 180, fontSize: '0.9rem' }}
                                    size="small"
                                    onChange={(e) => setAllShippingProvider(e.target.value ?? '')}
                                >
                                    {shippingProviders.map((provider) => (
                                        <MenuItem
                                            key={provider.id}
                                            value={provider.id}
                                            style={{ fontSize: '0.9rem' }}
                                        >
                                            {provider.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </div>
                        </div>
                    )}
                    {loading && (
                        <>
                            <Skeleton
                                variant="rectangular"
                                height={40}
                                style={{ marginBottom: 5 }}
                            />
                            <Skeleton
                                variant="rectangular"
                                height={40}
                                style={{ marginBottom: 5 }}
                            />
                            <Skeleton variant="rectangular" height={40} />
                        </>
                    )}
                    {!loading && (
                        <DndContext
                            collisionDetection={closestCenter}
                            onDragStart={handleDragStart}
                            onDragEnd={handleDragEnd}
                            onDragOver={handleDragOver}
                        >
                            <div style={{ display: 'flex', gap: '1rem', flexDirection: 'column' }}>
                                {Object.entries(fulfillmentSections).map(
                                    ([sectionId, fulfillment]) => (
                                        <DroppableSection
                                            key={sectionId}
                                            sectionId={sectionId}
                                            fulfillment={fulfillment}
                                            isDragOver={hoveringOverItem?.sectionId === sectionId}
                                            shippingProviders={shippingProviders}
                                            onShippingProviderChange={(value) =>
                                                handleShippingProviderChange(sectionId, value)
                                            }
                                        />
                                    )
                                )}
                            </div>

                            <div style={{ display: 'flex', gap: '1em', marginTop: '1em' }}>
                                <Action
                                    key="new_fulfillment"
                                    sectionId="new_fulfillment"
                                    text={t('new_fullfillment')}
                                    isDragOver={hoveringOverItem?.sectionId === 'new_fulfillment'}
                                    icon={<FileCopyIcon />}
                                />
                                <Action
                                    key="split_equally"
                                    sectionId="split_equally"
                                    text={t('split_equally')}
                                    isDragOver={hoveringOverItem?.sectionId === 'split_equally'}
                                    icon={<FlipToFrontIcon />}
                                />
                            </div>
                        </DndContext>
                    )}

                    <QuantityDialog
                        open={quantityDialogOpen}
                        defaultQuantity={1}
                        maxQuantity={activeFulfillmentLineItem?.quantity}
                        onClose={closeQuantityDialog}
                    />
                </div>
            </DialogContent>
            {!loading && (
                <DialogActions
                    sx={{
                        px: 2,
                        pb: 1.25,
                        width: '100%',
                        display: 'flex',
                        justifyContent: 'space-between',
                    }}
                >
                    <Button variant="outlined" color="secondary" onClick={mergeAllFulfillments}>
                        {t('merge_all')}
                    </Button>

                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <LoadingButton
                            variant="contained"
                            color="secondary"
                            loading={submitting}
                            onClick={handleSaveFulfillments}
                        >
                            {t('save')}
                        </LoadingButton>

                        <Button color="secondary" onClick={onClose}>
                            {t('cancel')}
                        </Button>
                    </Box>
                </DialogActions>
            )}
        </Dialog>
    )
}

function Action({ sectionId, text, isDragOver, icon }) {
    const { setNodeRef } = useDroppable({
        id: sectionId,
        data: { sectionId: sectionId },
    })

    return (
        <Box
            ref={setNodeRef}
            sx={{
                border: isDragOver ? '2px solid' : '1px solid',
                borderColor: (theme) => (isDragOver ? theme.palette.secondary.main : '#ccc'),
                borderRadius: 4,
                p: 1,
                minHeight: '100px',
                width: '100%',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
            }}
        >
            <span style={{ color: grey[500], marginRight: 4 }}>{icon}</span> <span>{text}</span>
        </Box>
    )
}

function DroppableSection({
    sectionId,
    fulfillment,
    isDragOver,
    shippingProviders,
    onShippingProviderChange,
}) {
    const { t } = useTranslation(['fulfillment_split'])

    const { setNodeRef } = useDroppable({
        id: sectionId,
        data: { sectionId },
    })

    const items = fulfillment.fulfillment_line_items || []

    const hasAssignedShippingProvider =
        fulfillment.is_shipping_created_externally && fulfillment.shipping_provider?.id

    return (
        <Box
            ref={setNodeRef}
            sx={{
                border: isDragOver ? '2px solid' : '1px solid',
                borderColor: (theme) => (isDragOver ? theme.palette.secondary.main : '#ccc'),
                borderRadius: 4,
                padding: 1,
                width: '100%',
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    mb: 1,
                    alignItems: 'center',
                    justifyContent: 'space-between',
                }}
            >
                <div style={{ width: '15%', color: grey[500] }}>{t('fulfillment')}</div>
                <div style={{ width: '40%' }}>
                    <span>{fulfillment.fulfillment_id}</span>
                </div>

                <div style={{ width: '20%', color: grey[500] }}>{t('shipping_provider')}</div>
                <div style={{ width: '25%' }}>
                    {hasAssignedShippingProvider ? (
                        <div style={{ textAlign: 'right' }}>
                            {fulfillment.shipping_provider?.name}
                        </div>
                    ) : (
                        <Select
                            label={t('shipping_provider')}
                            value={fulfillment?.shipping_provider?.id || ''}
                            style={{ width: '100%', fontSize: '0.9rem' }}
                            size="small"
                            onChange={(e) => onShippingProviderChange(e.target.value ?? '')}
                        >
                            {shippingProviders.map((provider) => (
                                <MenuItem
                                    key={provider.id}
                                    value={provider.id}
                                    style={{ fontSize: '0.9rem' }}
                                >
                                    {provider.name}
                                </MenuItem>
                            ))}
                        </Select>
                    )}
                </div>
            </Box>
            <SortableContext items={items} strategy={rectSortingStrategy}>
                <Grid container spacing={1}>
                    {items.map((item) => (
                        <DraggableItem
                            key={item.id}
                            itemId={item.id}
                            sectionId={sectionId}
                            item={item}
                        />
                    ))}
                </Grid>
            </SortableContext>
        </Box>
    )
}

function DraggableItem({ itemId, sectionId, item }) {
    const { attributes, listeners, setNodeRef, transform, transition } = useDraggable({
        id: `${sectionId}.${itemId}`,
        data: { itemId, sectionId },
    })

    const style = {
        transform: transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : undefined,
        transition,
        padding: '0.5rem 1rem',
        border: '1px solid #ccc',
        borderRadius: 4,
        margin: '0.5rem',
        backgroundColor: 'white',
    }

    return (
        <Grid
            key={`${sectionId}.${itemId}`}
            item
            ref={setNodeRef}
            style={style}
            {...attributes}
            {...listeners}
        >
            {item.is_bundle ? (
                <BundleFulfillmenLineItem item={item} />
            ) : (
                <FulfillmenLineItem item={item} />
            )}
        </Grid>
    )
}

function BundleFulfillmenLineItem({ item }) {
    return (
        <Box sx={{ display: 'flex', mb: 2, gap: 8, marginBottom: 0, width: 270 }}>
            <Box sx={{ width: 50 }}>
                <Box
                    component="img"
                    src={item.image_url}
                    alt={item.name}
                    sx={{ width: 50, height: 50, mr: 0.5 }}
                />
            </Box>
            <Box sx={{ width: '100%' }}>
                <Box
                    sx={{
                        width: '100%',
                        height: 36,
                        fontSize: '0.8rem',
                        textOverflow: 'ellipsis',
                        overflow: 'hidden',
                    }}
                >
                    {item.name}
                </Box>
                <Box
                    sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
                >
                    <div>
                        <Typography variant="caption" color="text.secondary">
                            {item.sku}
                        </Typography>
                    </div>
                    <div>
                        <Typography variant="caption" color="text.secondary">
                            x {item.quantity}
                        </Typography>
                    </div>
                </Box>
            </Box>
        </Box>
    )
}

function FulfillmenLineItem({ item }) {
    const stockItem = item.stock_record?.stock_item

    return (
        <Box sx={{ display: 'flex', mb: 2, gap: 8, marginBottom: 0, width: 270 }}>
            <Box sx={{ width: 50 }}>
                <Box
                    component="img"
                    src={item.image_url}
                    alt={stockItem.name}
                    sx={{ width: 50, height: 50, mr: 0.5 }}
                />
            </Box>
            <Box sx={{ width: '100%' }}>
                <Box
                    sx={{
                        width: '100%',
                        height: 36,
                        fontSize: '0.8rem',
                        textOverflow: 'ellipsis',
                        overflow: 'hidden',
                    }}
                >
                    {stockItem.name}
                </Box>
                <Box
                    sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
                >
                    <div>
                        <Typography variant="caption" color="text.secondary">
                            {stockItem.sku}
                        </Typography>
                    </div>
                    <div>
                        <Typography variant="caption" color="text.secondary">
                            x {item.quantity}
                        </Typography>
                    </div>
                </Box>
            </Box>
        </Box>
    )
}

function QuantityDialog({ open, defaultQuantity, maxQuantity, onClose }) {
    const { t } = useTranslation(['fulfillment_split'])
    const [quantity, setQuantity] = useState(defaultQuantity ?? 1)

    return (
        <Dialog open={open} onClose={onClose}>
            <DialogContent>
                <TextField
                    autoFocus
                    margin="dense"
                    id="name"
                    label="Number of units"
                    type="number"
                    style={{ width: 200 }}
                    value={quantity}
                    variant="outlined"
                    inputProps={{ min: 1, max: maxQuantity }}
                    onChange={(e) => setQuantity(e.target.value)}
                />
            </DialogContent>
            <DialogActions>
                <Button color="secondary" onClick={() => onClose()}>
                    {t('cancel')}
                </Button>
                <Button color="secondary" onClick={() => onClose(Number(quantity))}>
                    {t('move')}
                </Button>
            </DialogActions>
        </Dialog>
    )
}
